import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Payments as CashIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  PersonOff as TerceiroIcon,
} from '@mui/icons-material';
import { topupRatesService, CashTopupRow, CashTopupTotais, CashTopupFilters } from '../../../services';
import { dayCv, todayCv } from '../../../utils/date';

const cve = (n: number) => `${n.toLocaleString('pt-PT')}$`;

const ORIGEM_LABEL: Record<string, string> = {
  WALLET_POS: 'Carteira (balcão)',
  WRISTBAND: 'Pulseira',
};

/**
 * Numerário recebido — carteira ao balcão e pulseiras.
 *
 * Responde às três perguntas que o registo existe para responder: quanto entrou
 * em dinheiro, por que mão, e o que ficou por liquidar.
 */
export default function NumerarioPage() {
  const hoje = todayCv();
  const ha30 = dayCv(new Date(Date.now() - 30 * 864e5));

  const [linhas, setLinhas] = useState<CashTopupRow[]>([]);
  const [totais, setTotais] = useState<CashTopupTotais | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);

  const [filtros, setFiltros] = useState<CashTopupFilters>({ from: ha30, to: hoje });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await topupRatesService.cashTopups({ ...filtros, page: page + 1, limit });
      setLinhas(r.linhas);
      setTotais(r.totais);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ??
        'Não foi possível carregar. Confirme que a migração run_20260804_cash_limits foi executada.',
      );
    } finally {
      setLoading(false);
    }
  }, [filtros, page, limit]);

  useEffect(() => { load(); }, [load]);

  const setF = (k: keyof CashTopupFilters, v: any) => {
    setPage(0);
    setFiltros((f) => ({ ...f, [k]: v === '' ? undefined : v }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <CashIcon color="primary" /> Numerário recebido
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Carregamentos em dinheiro, de carteira ao balcão e de pulseiras em eventos.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Totais do período filtrado, não da página */}
      {totais && (
        <Grid container spacing={2}>
          {[
            { label: 'Operações', valor: String(totais.operacoes), destaque: false },
            { label: 'Recebido em dinheiro', valor: cve(totais.recebido), destaque: true },
            { label: 'Taxa cobrada', valor: cve(totais.taxa), destaque: false },
            { label: 'Creditado a clientes', valor: cve(totais.creditado), destaque: false },
            { label: 'Por liquidar', valor: cve(totais.aLiquidar), destaque: totais.aLiquidar > 0 },
          ].map((c) => (
            <Grid key={c.label} size={{ xs: 6, sm: 4, md: 2.4 }}>
              <Card variant="outlined" sx={c.destaque ? { bgcolor: 'primary.50' } : undefined}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                    color={c.destaque ? 'primary.main' : 'text.primary'}
                  >
                    {c.valor}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {totais && (totais.porTerceiros > 0 || totais.estornados > 0) && (
        <Alert severity="info">
          {totais.porTerceiros > 0 && (
            <>Neste período houve <strong>{totais.porTerceiros}</strong> carregamento(s) entregues por
              alguém que não o titular. </>
          )}
          {totais.estornados > 0 && (
            <><strong>{totais.estornados}</strong> estornado(s), já descontados dos totais acima.</>
          )}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
                value={filtros.from ?? ''} onChange={(e) => setF('from', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
                value={filtros.to ?? ''} onChange={(e) => setF('to', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Origem"
                value={filtros.origem ?? ''} onChange={(e) => setF('origem', e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="WALLET_POS">Carteira (balcão)</MenuItem>
                <MenuItem value="WRISTBAND">Pulseira</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Estornados"
                value={filtros.estornados ?? ''} onChange={(e) => setF('estornados', e.target.value)}>
                <MenuItem value="">Incluir</MenuItem>
                <MenuItem value="nao">Excluir</MenuItem>
                <MenuItem value="so">Só estornados</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" label="Operador"
                value={filtros.operador ?? ''} onChange={(e) => setF('operador', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField fullWidth size="small" label="Agente"
                value={filtros.agente ?? ''} onChange={(e) => setF('agente', e.target.value)} />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : linhas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Sem carregamentos no período.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Referência</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell align="right">Recebido</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                    <TableCell align="right">Creditado</TableCell>
                    <TableCell>Quem entregou</TableCell>
                    <TableCell>Financiamento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((r) => {
                    const estornado = r.estornadoEm != null;
                    return (
                      <TableRow key={r.referencia} hover sx={estornado ? { opacity: 0.55 } : undefined}>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {new Date(r.criadoEm).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {r.referencia}
                          {estornado && (
                            <Tooltip title={r.estornoMotivo ?? 'Estornado'}>
                              <Chip size="small" icon={<UndoIcon />} label="estornado" color="warning"
                                variant="outlined" sx={{ ml: 1, fontSize: 10 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{ORIGEM_LABEL[r.origem] ?? r.origem}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {cve(r.recebido)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {cve(r.taxa)}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            {(r.rateBps / 100).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {cve(r.creditado)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {r.portadorETitular === false ? (
                            <Tooltip title={
                              [r.portadorNome, r.portadorDoc, r.portadorNif && `NIF ${r.portadorNif}`]
                                .filter(Boolean).join(' · ') || 'Terceiro sem identificação registada'
                            }>
                              <Chip size="small" icon={<TerceiroIcon />} color="warning" variant="outlined"
                                label={r.portadorNome ?? 'Terceiro'} sx={{ fontSize: 10 }} />
                            </Tooltip>
                          ) : r.portadorETitular === true ? (
                            <Typography variant="caption" color="text.secondary">Titular</Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={r.financiamento === 'AGENT_FLOAT' ? 'Float' : 'A liquidar'}
                            color={r.financiamento === 'AGENT_FLOAT' ? 'success' : 'warning'}
                            variant="outlined"
                            sx={{ fontSize: 10 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totais?.operacoes ?? 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Linhas"
          />
        </CardContent>
      </Card>
    </Box>
  );
}
